'use client';
/**
 * RAPPEL — UNE RUBRIQUE EST FERMÉE POUR UN MOTIF QUI N'EST PLUS D'ACTUALITÉ.
 *
 * ── Ce que ce bandeau existe pour éviter ───────────────────────────────────
 *
 * Fermer une rubrique se fait avec un motif : « aucune évaluation externe n'a
 * encore été saisie, à ouvrir dès le retour du premier envoi ». Le motif décrit
 * un état du monde, et cet état change — sans que rien ne le dise. Constaté le
 * 28/08/2026 : la condition d'ouverture de l'EEQ était remplie depuis le matin,
 * et la rubrique serait restée fermée indéfiniment, parce que personne ne relit
 * les motifs des rubriques qu'il ne voit pas.
 *
 * ⚠️ IL NE DIT PAS « OUVREZ ». Il confronte deux choses — ce qui avait été
 * écrit, et ce qui est vrai aujourd'hui — et s'arrête là. Ouvrir reste une
 * décision : un outil qui la prend à votre place se fait désactiver au bout de
 * trois fois, et emporte avec lui les rappels utiles.
 *
 * ⚠️ ET IL RAPPELLE LE MOTIF, pas seulement le compte. « 4 lignes existent »
 * ne dit pas ce qu'on avait décidé ; la décision se reprendrait à l'aveugle.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { I, card } from '@/components/ui';

type Rappel = { code: string; label: string; motif: string; compte: number; phrase: string };

export function RappelsFermeture() {
  const { interroger, session } = useGed();
  /* ⚠️ LE PROFIL, pas le rôle (10/09/2026). La mise à disposition des modules
     est passée au super administrateur : montrer ce rappel à un responsable
     qualité l'inviterait à un geste que la route lui refusera. Informer qui ne
     peut pas agir n'est pas informer. */
  const estRQ = session.profile === 'superadmin';
  const [liste, setListe] = React.useState<Rappel[] | null>(null);

  React.useEffect(() => {
    // La mise à disposition relève du SUPER ADMIN : personne d'autre ne peut
    // agir sur ce rappel, et le montrer à qui ne peut rien en faire n'est pas
    // informer.
    /* ⚠️ ON N'ÉCRIT PLUS `[]` DANS L'ÉTAT pour dire « rien à montrer »
       (02/09/2026). C'était un état qui n'apportait rien : la condition est
       connue au rendu, et le repli plus bas (`!liste || !liste.length`) rend
       déjà `null`. Écrire pour redire ce qu'on sait déjà, c'est un rendu de
       plus et une vérité de plus à tenir à jour. */
    if (!estRQ) return undefined;
    let vivant = true;
    void (async () => {
      const r = await interroger<{ rappels?: Rappel[] }>('ged/deploiement', undefined);
      if (vivant) setListe(r?.rappels || []);
    })();
    return () => { vivant = false; };
  }, [estRQ, interroger]);

  // ⚠️ Rien tant qu'on ne sait pas, et rien quand il n'y a rien. Un cadre vide
  // « aucun rappel » au tableau de bord serait du bruit permanent.
  if (!estRQ || !liste || !liste.length) return null;

  return (
    <div style={{
      ...card, padding: '12px 15px', display: 'grid', gap: 8,
      background: 'var(--amber-50, #FFF8EC)', borderColor: 'var(--amber-300, #EFC77B)',
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
        <I n="alert" s={15} />
        {liste.length === 1
          ? 'Une rubrique est fermée pour un motif qui n’est plus d’actualité.'
          : `${liste.length} rubriques sont fermées pour des motifs qui ne sont plus d’actualité.`}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {liste.map((r) => (
          <div key={r.code} style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
            {r.phrase}
            {/* Le motif EXACT, tel qu'il a été écrit, entre guillemets : c'est
                lui qu'on relit pour décider, et le reformuler le déformerait. */}
            {r.motif && (
              <span style={{ display: 'block', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Motif écrit à la fermeture : « {r.motif} »
              </span>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
        Une rubrique peut rester fermée à bon droit : ceci ne dit pas qu’il faut l’ouvrir, seulement
        que le fait sur lequel la décision reposait a changé. Le déploiement se règle au paramétrage.
      </div>
    </div>
  );
}
